import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { StatusBadge } from '@/components/StatusBadge';
import { formatTime, loadNumber } from '../format';
import type { SimTura } from './api';

/** The tracking phase's label; the tracker's 'at_stop' has no kind here, so it reads "At stop" */
const phaseLabelKey = (phase: string) => `fleet.tracking.phase.${phase === 'at_stop' ? 'atStop' : phase}`;

/** An event kind's label: pause and rest are tracking's own, the rest are the simulation's */
const eventLabelKey = (kind: string) => (kind === 'pause' || kind === 'rest' ? `fleet.tracking.event.${kind}` : `fleet.simulation.event.${kind}`);

/** One copy of a tura: what the replay made of it so far */
export function SimTuraRow({ tura }: { tura: SimTura }) {
  const { t, i18n } = useTranslation();
  const at = (iso: string) => formatTime(new Date(iso), i18n.language);

  return (
    <li className="space-y-1 rounded-md border p-2" data-qa-id="simulation-tura">
      <div className="flex flex-wrap items-center gap-1.5">
        <Link to={`/loads?load=${tura.id}`} className="text-sm font-medium hover:underline">
          {loadNumber(tura.number)}
        </Link>
        <StatusBadge status={tura.status} size="sm" dot={false}>
          {t(`fleet.loadStatus.${tura.status}`)}
        </StatusBadge>
        {tura.phase && (
          <StatusBadge tone="neutral" size="sm" dot={false}>
            {t(phaseLabelKey(tura.phase), { defaultValue: tura.phase })}
          </StatusBadge>
        )}
        {tura.flags.map((flag) => (
          <StatusBadge key={flag} tone="warning" size="sm" dot={false}>
            {t(`fleet.tracking.flag.${flag}`, { defaultValue: flag })}
          </StatusBadge>
        ))}
      </div>
      {tura.events.length > 0 && (
        <ul className="text-xs text-muted-foreground">
          {tura.events.map((event, index) => (
            <li key={`${event.at}-${index}`} className="tabular-nums">
              {at(event.at)} {t(eventLabelKey(event.kind), { defaultValue: event.kind })}
            </li>
          ))}
        </ul>
      )}
      {tura.claude && (
        <p className="line-clamp-3 text-xs" title={tura.claude.text}>
          <span className="font-medium">{t('fleet.simulation.claude')}</span> <span className="text-muted-foreground tabular-nums">{at(tura.claude.at)}</span> {tura.claude.text}
        </p>
      )}
    </li>
  );
}
